import React from 'react';
import {
  DocumentTextIcon,
  PencilIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  CpuChipIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

export default function EditAppraisalHelp() {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-purple-600 p-3 rounded-full">
              <PencilIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Edit Appraisal Guide</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Master the art of reviewing, editing, and managing employee performance appraisals with advanced AI analysis and collaborative features.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <EyeIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Edit Appraisal interface provides comprehensive tools for reviewing, modifying, and managing
            employee performance evaluations. With AI-powered analysis, collaborative feedback mechanisms,
            and detailed tracking capabilities, managers can conduct thorough performance reviews.
          </p>
        </div>

        {/* Key Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Appraisal Management */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Appraisal Management</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Multiple Appraisals:</strong> View and manage multiple performance reviews by date</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Edit Fields:</strong> Modify any section of the appraisal with inline editing</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Delete Appraisals:</strong> Remove outdated or incorrect performance reviews</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Status Tracking:</strong> Monitor appraisal completion and employee feedback status</span>
              </li>
            </ul>
          </div>

          {/* AI Analysis */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <CpuChipIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">AI-Powered Analysis</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Smart Analysis:</strong> AI analyzes appraisal content for insights and improvements</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Rewrite Suggestions:</strong> Get AI-generated improvements for appraisal text</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Apply Changes:</strong> Seamlessly integrate AI suggestions into the appraisal</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Contextual Analysis:</strong> AI considers the entire appraisal context for recommendations</span>
              </li>
            </ul>
          </div>

          {/* Collaboration Features */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Collaboration Tools</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Employee Comments:</strong> Send appraisals for employee feedback and self-assessment</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Meeting Notes:</strong> Record and review discussion points from appraisal meetings</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Search Functionality:</strong> Quickly find specific information within appraisals and notes</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Completion Tracking:</strong> Mark appraisals as completed and track follow-up actions</span>
              </li>
            </ul>
          </div>
        </div>

        {/* How to Use */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">How to Edit Appraisals</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 Basic Editing</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  <span>Select an appraisal from the date tabs at the top of the page</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  <span>Click the "Edit" button next to any field you want to modify</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  <span>Make your changes in the text area that appears</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  <span>Click "Save" to apply your changes</span>
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Enhancement</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  <span>Click "AI Analyze" next to any text field</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  <span>Review the AI-generated suggestions in the right panel</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  <span>Edit the AI suggestions if needed</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  <span>Click "Apply Rewrite to Section" to integrate the changes</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-8 text-white mb-8">
          <div className="flex items-center mb-6">
            <ClockIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Appraisal Actions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-4 mb-3">
                <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto mb-2" />
              </div>
              <h3 className="font-semibold mb-2">Send for Comments</h3>
              <p className="text-purple-100 text-sm">Share appraisal with employee for feedback and self-assessment</p>
            </div>

            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-4 mb-3">
                <DocumentTextIcon className="h-8 w-8 mx-auto mb-2" />
              </div>
              <h3 className="font-semibold mb-2">Meeting Notes</h3>
              <p className="text-purple-100 text-sm">Access and review notes from appraisal discussion meetings</p>
            </div>

            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-4 mb-3">
                <CheckCircleIcon className="h-8 w-8 mx-auto mb-2" />
              </div>
              <h3 className="font-semibold mb-2">Mark Complete</h3>
              <p className="text-purple-100 text-sm">Finalize the appraisal and track completion status</p>
            </div>

            <div className="text-center">
              <div className="bg-white/20 rounded-lg p-4 mb-3">
                <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2" />
              </div>
              <h3 className="font-semibold mb-2">Search & Filter</h3>
              <p className="text-purple-100 text-sm">Find specific information within appraisals and meeting notes</p>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Best Practices for Appraisal Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">✨ Quality Assurance</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use AI analysis to improve appraisal quality and consistency</li>
                <li>• Review all sections before sending to employees</li>
                <li>• Ensure ratings align with documented evidence</li>
                <li>• Maintain professional and constructive language throughout</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔄 Continuous Improvement</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Schedule regular follow-up meetings to discuss progress</li>
                <li>• Use meeting notes to track ongoing development discussions</li>
                <li>• Update appraisals as employee performance evolves</li>
                <li>• Leverage AI suggestions for continuous improvement</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Data Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Keep detailed records of all appraisal changes and updates</li>
                <li>• Use search functionality to quickly find historical information</li>
                <li>• Maintain chronological order of performance reviews</li>
                <li>• Archive completed appraisals for future reference</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🤝 Employee Engagement</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Encourage employee participation through comment requests</li>
                <li>• Create a supportive environment for open dialogue</li>
                <li>• Use appraisals as development opportunities, not just evaluations</li>
                <li>• Follow up on action items and development goals</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}